import { DEFAULT_REGION, getRegionForApi } from '@/utils/api'
import { SINGLE_SCHEMA_ENABLED } from '@/config/singleSchema'

/** Region slug → numeric region_id (matches backend regions table). */
export const REGION_ID_MAP: Record<string, number> = {
  johannesburg: 1,
  johannesburg_go: 1,
  johannesburg_eats: 1,
  cape_town: 2,
  durban: 3,
}

export const REGION_CODE_BY_ID: Record<number, string> = {
  1: 'johannesburg',
  2: 'cape_town',
  3: 'durban',
}

/** Default region for new Assets (Our Vehicles → Add Asset). */
export const DEFAULT_ASSET_REGION_ID = 2
export const DEFAULT_ASSET_REGION_CODE = REGION_CODE_BY_ID[DEFAULT_ASSET_REGION_ID]

export function isSingleSchemaEnabled(): boolean {
  return SINGLE_SCHEMA_ENABLED
}

export function regionIdFromCode(regionCode?: string | null): number | null {
  const code = normalizeRegionCode(regionCode ?? getRegionForApi())
  return REGION_ID_MAP[code] ?? null
}

export function regionCodeFromId(regionId?: number | null): string | null {
  if (regionId == null) return null
  return REGION_CODE_BY_ID[regionId] ?? null
}

export function normalizeRegionCode(raw: string): string {
  const lower = String(raw).toLowerCase().trim().replace(/\s+/g, '_')
  if (lower.startsWith('johannesburg')) return 'johannesburg'
  if (lower.startsWith('cape_town')) return 'cape_town'
  if (lower.startsWith('durban')) return 'durban'
  return lower || DEFAULT_REGION
}

export function currentRegionId(): number | null {
  return regionIdFromCode(getRegionForApi())
}

/**
 * Append region scoping params for API calls.
 * Single-schema: prefers region_id; legacy: uses region slug.
 */
export function appendRegionScopeToParams(params: URLSearchParams, regionOverride?: string): URLSearchParams {
  if (isSingleSchemaEnabled()) {
    const regionId = regionIdFromCode(regionOverride)
    if (regionId != null) {
      params.set('region_id', String(regionId))
    }
    // Keep region slug for backward compatibility during rollout
    const slug = regionOverride ?? getRegionForApi()
    if (slug) params.set('region', slug)
  } else {
    const slug = regionOverride ?? getRegionForApi()
    if (slug) params.set('region', slug)
  }
  return params
}

export function regionScopeHeaders(regionOverride?: string): Record<string, string> {
  const headers: Record<string, string> = {}
  const slug = regionOverride ?? getRegionForApi()
  if (slug) headers['X-Region'] = slug

  if (isSingleSchemaEnabled()) {
    const regionId = regionIdFromCode(regionOverride)
    if (regionId != null) {
      headers['X-Region-Id'] = String(regionId)
    }
  }

  return headers
}

/** Region headers only when an explicit region slug is provided — no localStorage default. */
export function optionalRegionScopeHeaders(region?: string | null): Record<string, string> {
  const slug = region?.trim()
  if (!slug) return {}
  return regionScopeHeaders(slug)
}
